import { useEffect, useMemo, useState } from 'react'
import type { RouteStatus } from '@xingroute/schema'
import { api } from '../api/client.js'
import { trailColorForThumbnail } from '../lib/trailColor.js'
import { useTheme } from '../theme/ThemeContext.js'

// RouteThumbMap — the card thumbnail on the routes list page.
//
// Uses Mapbox Static Images API (a single PNG URL) instead of mounting a
// live mapbox-gl Map per card. This keeps the list page snappy regardless of
// how many cards are rendered; the browser caches each URL automatically.
//
// Style choice: Classic Mapbox Light v11 / Dark v11 (the data-viz pair). They
// are deliberately frozen — picked over Standard so the cached thumbnail
// stays visually identical for years even as Mapbox evolves Standard. Per
// `wiki/decisions/06-design-system.md` §Trail colour policy the thumbnail
// polyline uses Brand Moss / logo green (#1a5a44) for published / archived /
// unknown routes and switches to a neutral muted grey (#6b7280) only for
// draft / unpublished thumbnails. Thumbnails render no markers, so green here
// can't collide with the green start dot / waypoint markers that interactive
// maps reserve it for (those keep Accent Twilight trails). Callers can opt
// into a satellite variant via `variant="satellite"` when the surrounding
// card already implies aerial context.

const MAPBOX_TOKEN = (
  import.meta.env.VITE_MAPBOX_KEY ??
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ??
  ''
).trim()
const W = 640
const H = 320
export const MIN_THUMB_ZOOM = 12
export const MAX_THUMB_ZOOM = 16
const WEB_MERCATOR_TILE_SIZE = 512
const MIN_SPAN = 1e-9

type ThumbVariant = 'auto' | 'satellite'

interface Props {
  routeId?: string
  hasGeometry: boolean
  points?: RouteStaticMapPoint[]
  drawLine?: boolean
  fallbackUrl?: string
  /** `auto` (default) follows the active theme via Mapbox light-v11 /
   *  dark-v11. `satellite` forces the classic satellite-v9 base. */
  variant?: ThumbVariant
  className?: string
  status?: RouteStatus | null
}

export interface RouteStaticMapPoint {
  lat: number
  lon: number
}

export function RouteThumbMap({
  routeId,
  hasGeometry,
  points,
  drawLine: suppliedDrawLine,
  fallbackUrl,
  variant = 'auto',
  className,
  status,
}: Props) {
  const [track, setTrack] = useState<RouteStaticMapPoint[] | null>(null)
  const [drawLine, setDrawLine] = useState(suppliedDrawLine ?? true)
  const [failed, setFailed] = useState(false)
  const { effective } = useTheme()

  useEffect(() => {
    if (!hasGeometry || points || !routeId) return
    let cancelled = false
    api
      .getRouteGeometry(routeId)
      .then(({ geometry }) => {
        if (cancelled) return
        const source = geometry.line.length > 0 ? geometry.line : (geometry.points ?? [])
        setDrawLine(geometry.line.length > 0)
        const points: RouteStaticMapPoint[] = source.map((p) => ({
          lat: p.lat,
          lon: p.lon,
        }))
        setTrack(points)
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [routeId, hasGeometry, points])

  useEffect(() => {
    if (points) {
      setTrack(points)
      setDrawLine(suppliedDrawLine ?? true)
    }
  }, [points, suppliedDrawLine])

  const staticUrl = useMemo(() => {
    return buildRouteStaticMapUrl(track, { variant, effective, status, drawLine })
  }, [drawLine, track, variant, effective, status])

  if (!hasGeometry || failed || !MAPBOX_TOKEN) {
    return fallbackUrl ? (
      <div
        aria-hidden="true"
        data-testid="route-thumb-map"
        tabIndex={-1}
        className={
          className ?? 'aspect-[2/1] w-full overflow-hidden bg-[var(--xr-bg-surface-elev)]'
        }
      >
        <img src={fallbackUrl} alt="" className="h-full w-full object-cover" />
      </div>
    ) : (
      <Placeholder className={className} />
    )
  }
  if (!staticUrl) {
    return <Placeholder loading className={className} />
  }

  return (
    <div
      aria-hidden="true"
      data-testid="route-thumb-map"
      tabIndex={-1}
      className={`relative ${className ?? 'aspect-[2/1] w-full overflow-hidden bg-[var(--xr-bg-surface-elev)]'}`}
    >
      {/* Shift the image up slightly so the bottom attribution pixels are cropped by overflow-hidden */}
      <img
        src={staticUrl}
        alt=""
        className="h-[108%] w-full object-cover object-center"
        loading="lazy"
      />
    </div>
  )
}

function Placeholder({ loading, className }: { loading?: boolean; className?: string } = {}) {
  return (
    <div
      aria-hidden="true"
      data-testid="route-thumb-map"
      tabIndex={-1}
      className={
        className ??
        'flex aspect-[2/1] w-full items-center justify-center bg-[var(--xr-bg-surface-elev)] text-xs text-[var(--xr-text-muted)]'
      }
    >
      {loading ? '…' : '—'}
    </div>
  )
}

function styleSlugFor(variant: ThumbVariant, effective: 'light' | 'dark'): string {
  if (variant === 'satellite') return 'satellite-v9'
  return effective === 'dark' ? 'dark-v11' : 'light-v11'
}

export function buildRouteStaticMapUrl(
  track: RouteStaticMapPoint[] | null | undefined,
  {
    variant = 'auto',
    effective = 'light',
    width = W,
    height = H,
    padding = 20,
    maxPoints = 100,
    status,
    drawLine = true,
  }: {
    variant?: ThumbVariant
    effective?: 'light' | 'dark'
    width?: number
    height?: number
    padding?: number
    maxPoints?: number
    status?: RouteStatus | null
    drawLine?: boolean
  } = {},
): string | null {
  if (!track || track.length < 1 || !MAPBOX_TOKEN) return null
  const color = trailColorForThumbnail(status).replace(/^#/, '')
  if (track.length === 1 || !drawLine) {
    const only = track[0]!
    const overlay = drawLine
      ? `pin-s+${color}(${only.lon},${only.lat})`
      : simplifyEveryNth(track, maxPoints)
          .map((point) => `pin-s+${color}(${roundCoord(point.lon)},${roundCoord(point.lat)})`)
          .join(',')
    const slug = styleSlugFor(variant, effective)
    const base = `https://api.mapbox.com/styles/v1/mapbox/${slug}/static`
    const { center, zoom } =
      track.length === 1
        ? { center: only, zoom: 11 }
        : computeRouteThumbViewport(track, { width, height, padding })
    return `${base}/${overlay}/${roundCoord(center.lon)},${roundCoord(center.lat)},${roundZoom(zoom)},0/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`
  }
  const simplified = simplifyEveryNth(track, maxPoints)
  const encoded = encodePolyline(simplified)
  const overlay = `path-4+${color}-1(${encodeURIComponent(encoded)})`
  const slug = styleSlugFor(variant, effective)
  const base = `https://api.mapbox.com/styles/v1/mapbox/${slug}/static`
  const { center, zoom } = computeRouteThumbViewport(track, { width, height, padding })
  const camera = `${roundCoord(center.lon)},${roundCoord(center.lat)},${roundZoom(zoom)},0`
  return `${base}/${overlay}/${camera}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`
}

function simplifyEveryNth(points: RouteStaticMapPoint[], maxPoints: number): RouteStaticMapPoint[] {
  if (points.length <= maxPoints) return points
  const step = (points.length - 1) / (maxPoints - 1)
  const out: RouteStaticMapPoint[] = []
  for (let i = 0; i < maxPoints; i++) {
    out.push(points[Math.round(i * step)]!)
  }
  return out
}

function encodePolyline(points: RouteStaticMapPoint[]): string {
  let prevLat = 0
  let prevLon = 0
  let out = ''
  for (const p of points) {
    const lat = Math.round(p.lat * 1e5)
    const lon = Math.round(p.lon * 1e5)
    out += encodeSigned(lat - prevLat)
    out += encodeSigned(lon - prevLon)
    prevLat = lat
    prevLon = lon
  }
  return out
}

function encodeSigned(n: number): string {
  let v = n < 0 ? ~(n << 1) : n << 1
  let out = ''
  while (v >= 0x20) {
    out += String.fromCharCode((0x20 | (v & 0x1f)) + 63)
    v >>>= 5
  }
  out += String.fromCharCode(v + 63)
  return out
}

export function computeRouteThumbViewport(
  track: RouteStaticMapPoint[],
  {
    width = W,
    height = H,
    padding = 20,
  }: {
    width?: number
    height?: number
    padding?: number
  } = {},
): { center: RouteStaticMapPoint; computedZoomToFit: number; zoom: number } {
  const bounds = boundsFor(track)
  const center = {
    lon: (bounds.minLon + bounds.maxLon) / 2,
    lat: unprojectMercatorY(
      (projectMercatorY(bounds.minLat) + projectMercatorY(bounds.maxLat)) / 2,
    ),
  }
  const usableWidth = Math.max(1, width - padding * 2)
  const usableHeight = Math.max(1, height - padding * 2)
  const lonSpan = Math.max(MIN_SPAN, bounds.maxLon - bounds.minLon)
  const mercatorSpan = Math.max(
    MIN_SPAN,
    Math.abs(projectMercatorY(bounds.maxLat) - projectMercatorY(bounds.minLat)),
  )
  const zoomX = Math.log2((usableWidth * 360) / (lonSpan * WEB_MERCATOR_TILE_SIZE))
  const zoomY = Math.log2(usableHeight / (mercatorSpan * WEB_MERCATOR_TILE_SIZE))
  const computedZoomToFit = Math.min(zoomX, zoomY)
  return {
    center,
    computedZoomToFit,
    zoom: clamp(computedZoomToFit, MIN_THUMB_ZOOM, MAX_THUMB_ZOOM),
  }
}

function boundsFor(track: RouteStaticMapPoint[]) {
  let minLat = Infinity
  let maxLat = -Infinity
  let minLon = Infinity
  let maxLon = -Infinity
  for (const p of track) {
    minLat = Math.min(minLat, p.lat)
    maxLat = Math.max(maxLat, p.lat)
    minLon = Math.min(minLon, p.lon)
    maxLon = Math.max(maxLon, p.lon)
  }
  return { minLat, maxLat, minLon, maxLon }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function projectMercatorY(lat: number): number {
  const boundedLat = clamp(lat, -85.05112878, 85.05112878)
  const sin = Math.sin((boundedLat * Math.PI) / 180)
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)
}

function unprojectMercatorY(y: number): number {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI
}

function roundCoord(value: number): string {
  return value.toFixed(6).replace(/\.?0+$/, '')
}

function roundZoom(value: number): string {
  return value.toFixed(2).replace(/\.?0+$/, '')
}
